import {
  LOCATION_SOURCE_BROWSER_GEOLOCATION,
  getGeolocationOptions,
} from "../config/locationAssist";
import { bucketAccuracyMeters } from "./location/routingEvidence";

export const ENVIRONMENTAL_CONTEXT_SCHEMA_VERSION = "environmental-context-v1";
export const ENVIRONMENTAL_PROVIDER_NAME = "google-maps-platform";

const DEFAULT_COORDINATE_DECIMALS = 2;
const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000;
const TRUTHY_FLAG_VALUES = new Set(["1", "true", "yes", "on", "enabled"]);

function parseBooleanFlag(value, fallback = false) {
  if (value === undefined || value === null) {
    return fallback;
  }

  return TRUTHY_FLAG_VALUES.has(String(value).trim().toLowerCase());
}

function parsePositiveInteger(value, fallback) {
  const parsedValue = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallback;
}

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue || null;
}

function normalizeFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeUppercaseString(value) {
  const normalizedValue = normalizeOptionalString(value);
  return normalizedValue ? normalizedValue.toUpperCase() : null;
}

function readBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_error) {
    return null;
  }
}

function floorToUtcHourIso(timestamp = new Date().toISOString()) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }

  date.setUTCMinutes(0, 0, 0);
  return date.toISOString();
}

function ceilToUtcHourIso(timestamp = new Date().toISOString()) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return floorToUtcHourIso();
  }

  if (
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0
  ) {
    date.setUTCHours(date.getUTCHours() + 1, 0, 0, 0);
    return date.toISOString();
  }

  date.setUTCMinutes(0, 0, 0);
  return date.toISOString();
}

function shiftIsoHours(timestamp, hours) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return floorToUtcHourIso();
  }

  date.setUTCHours(date.getUTCHours() + Number(hours || 0));
  return date.toISOString();
}

function readZonedDateParts(timestamp, timeZone) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: normalizeOptionalString(timeZone) || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const parts = formatter.formatToParts(date).reduce((accumulator, entry) => {
      if (entry.type !== "literal") {
        accumulator[entry.type] = entry.value;
      }
      return accumulator;
    }, {});

    return {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: Number.parseInt(parts.hour, 10),
      minute: Number.parseInt(parts.minute, 10),
    };
  } catch (_error) {
    return {
      year: String(date.getUTCFullYear()),
      month: String(date.getUTCMonth() + 1).padStart(2, "0"),
      day: String(date.getUTCDate()).padStart(2, "0"),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
    };
  }
}

function buildLocalDayKey(timestamp, timeZone) {
  const parts = readZonedDateParts(timestamp, timeZone);
  if (!parts) {
    return null;
  }

  return `${parts.year}-${parts.month}-${parts.day}`;
}

function buildCurrentHourOffset(timestamp, timeZone) {
  const parts = readZonedDateParts(timestamp, timeZone);
  if (!parts) {
    return null;
  }

  return parts.hour + parts.minute / 60;
}

function buildEmptyTimelineSlots() {
  return Array.from({ length: 24 }, (_entry, hour) => ({
    hour,
    value: null,
    timestamp: null,
    source: null,
    observationType: null,
    qualityStatus: null,
  }));
}

function roundCoordinate(value, decimals = DEFAULT_COORDINATE_DECIMALS) {
  const numericValue = normalizeFiniteNumber(value);
  if (numericValue === null) {
    return null;
  }

  const multiplier = 10 ** decimals;
  return Math.round(numericValue * multiplier) / multiplier;
}

function buildCoordinateGranularity(decimals = DEFAULT_COORDINATE_DECIMALS) {
  return `rounded_${decimals}dp`;
}

function readLanguageCode() {
  if (typeof document !== "undefined") {
    const documentLanguage = document.documentElement?.lang?.trim();
    if (documentLanguage) {
      return documentLanguage;
    }
  }

  if (typeof navigator !== "undefined") {
    return navigator.language || navigator.languages?.[0] || "en";
  }

  return "en";
}

function getGoogleMapsApiKey(env = process.env) {
  return normalizeOptionalString(env.REACT_APP_GOOGLE_MAPS_API_KEY);
}

export function isEnvironmentalContextEnabled(env = process.env) {
  return (
    parseBooleanFlag(env.REACT_APP_PDX_ENVIRONMENTAL_CONTEXT_ENABLED, true) &&
    Boolean(getGoogleMapsApiKey(env))
  );
}

function getEnvironmentalGeolocationOptions(env = process.env) {
  const baseOptions = getGeolocationOptions(env);

  return {
    ...baseOptions,
    timeout: parsePositiveInteger(
      env.REACT_APP_PDX_ENVIRONMENTAL_CONTEXT_TIMEOUT_MS,
      DEFAULT_TIMEOUT_MS,
    ),
    maximumAge: parsePositiveInteger(
      env.REACT_APP_PDX_ENVIRONMENTAL_CONTEXT_MAX_AGE_MS,
      DEFAULT_MAX_AGE_MS,
    ),
  };
}

function buildBaseContext({
  purpose,
  status,
  location = null,
  retrievedAt = new Date().toISOString(),
  observations = [],
  timelines = null,
  limitations = [],
  errors = [],
} = {}) {
  return {
    schemaVersion: ENVIRONMENTAL_CONTEXT_SCHEMA_VERSION,
    purpose: normalizeOptionalString(purpose),
    status: normalizeOptionalString(status) || "unavailable",
    provider: {
      name: ENVIRONMENTAL_PROVIDER_NAME,
      weatherApiVersion: "v1",
      airQualityApiVersion: "v1",
      pollenApiVersion: "v1",
    },
    retrievedAt,
    location,
    observations,
    timelines:
      timelines && typeof timelines === "object" && !Array.isArray(timelines)
        ? timelines
        : null,
    limitations: Array.from(
      new Set(
        (Array.isArray(limitations) ? limitations : [])
          .map((value) => normalizeOptionalString(value))
          .filter(Boolean),
      ),
    ),
    errors: (Array.isArray(errors) ? errors : [])
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return null;
        }

        return {
          source: normalizeOptionalString(entry.source),
          code: normalizeOptionalString(entry.code),
          message: normalizeOptionalString(entry.message),
        };
      })
      .filter(Boolean),
  };
}

function buildUnavailableContext({
  purpose,
  source,
  code,
  message,
  location = null,
} = {}) {
  return buildBaseContext({
    purpose,
    status: "unavailable",
    location,
    errors: [{ source, code, message }],
  });
}

function geolocationSupported(geolocation) {
  return (
    Boolean(geolocation) &&
    typeof geolocation.getCurrentPosition === "function"
  );
}

function resolveGeolocationErrorCode(error) {
  switch (error?.code) {
    case 1:
      return "denied";
    case 3:
      return "timeout";
    case 2:
    default:
      return "unavailable";
  }
}

async function getCurrentCoarseLocation({
  geolocation = typeof navigator !== "undefined" ? navigator.geolocation : undefined,
  env = process.env,
} = {}) {
  if (!geolocationSupported(geolocation)) {
    throw new Error("unsupported");
  }

  const decimals = parsePositiveInteger(
    env.REACT_APP_PDX_ENVIRONMENTAL_CONTEXT_COORDINATE_DECIMALS,
    DEFAULT_COORDINATE_DECIMALS,
  );

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(
      (position) => {
        const coords = position?.coords || {};
        const latitude = roundCoordinate(coords.latitude, decimals);
        const longitude = roundCoordinate(coords.longitude, decimals);

        if (latitude === null || longitude === null) {
          reject(new Error("unavailable"));
          return;
        }

        resolve({
          source: LOCATION_SOURCE_BROWSER_GEOLOCATION,
          latitude,
          longitude,
          accuracyMeters: normalizeFiniteNumber(coords.accuracy),
          accuracyBucket: bucketAccuracyMeters(coords.accuracy),
          granularity: buildCoordinateGranularity(decimals),
          locationObtainedAt: new Date().toISOString(),
        });
      },
      (error) => {
        reject(new Error(resolveGeolocationErrorCode(error)));
      },
      getEnvironmentalGeolocationOptions(env),
    );
  });
}

function buildObservation({
  variable,
  value,
  unit,
  observationTimestamp,
  retrievalTimestamp,
  timezone,
  observationType,
  source,
  sourceStationOrArea = null,
  aqiStandard = null,
  aggregationPeriod = null,
  qualityStatus = "available",
  datasetVersion = null,
  locationGranularity = null,
  provisional = false,
} = {}) {
  return {
    variable: normalizeOptionalString(variable),
    value: normalizeFiniteNumber(value),
    unit: normalizeOptionalString(unit),
    observationTimestamp: normalizeOptionalString(observationTimestamp),
    retrievalTimestamp: normalizeOptionalString(retrievalTimestamp),
    timezone: normalizeOptionalString(timezone),
    observationType: normalizeOptionalString(observationType),
    source: normalizeOptionalString(source),
    sourceStationOrArea: normalizeOptionalString(sourceStationOrArea),
    aqiStandard: normalizeOptionalString(aqiStandard),
    aggregationPeriod: normalizeOptionalString(aggregationPeriod),
    qualityStatus: normalizeOptionalString(qualityStatus),
    datasetVersion: normalizeOptionalString(datasetVersion),
    locationGranularity: normalizeOptionalString(locationGranularity),
    provisional: provisional === true,
  };
}

async function fetchJson(url, options = {}, fetchImpl = fetch) {
  const response = await fetchImpl(url, options);
  if (!response.ok) {
    throw new Error(`request_failed_${response.status}`);
  }

  return response.json();
}

async function lookupWeather(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({
    key: apiKey,
    "location.latitude": String(location.latitude),
    "location.longitude": String(location.longitude),
    languageCode,
  });

  const response = await fetchJson(
    `https://weather.googleapis.com/v1/currentConditions:lookup?${query.toString()}`,
    { method: "GET" },
    fetchImpl,
  );

  return response;
}

async function lookupAirQuality(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({ key: apiKey });
  const response = await fetchJson(
    `https://airquality.googleapis.com/v1/currentConditions:lookup?${query.toString()}`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
        languageCode,
        universalAqi: true,
        extraComputations: [
          "LOCAL_AQI",
          "POLLUTANT_CONCENTRATION",
          "POLLUTANT_ADDITIONAL_INFO",
        ],
      }),
    },
    fetchImpl,
  );

  return response;
}

async function lookupPollen(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({
    key: apiKey,
    "location.latitude": String(location.latitude),
    "location.longitude": String(location.longitude),
    languageCode,
    days: "1",
  });

  const response = await fetchJson(
    `https://pollen.googleapis.com/v1/forecast:lookup?${query.toString()}`,
    { method: "GET" },
    fetchImpl,
  );

  return response;
}

async function lookupLocationDetails(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({
    key: apiKey,
    "location.latitude": String(location.latitude),
    "location.longitude": String(location.longitude),
    languageCode,
  });

  return fetchJson(
    `https://geocode.googleapis.com/v4/geocode/location?${query.toString()}`,
    { method: "GET" },
    fetchImpl,
  );
}

async function lookupWeatherForecastHours(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({
    key: apiKey,
    "location.latitude": String(location.latitude),
    "location.longitude": String(location.longitude),
    languageCode,
    hours: "24",
    pageSize: "24",
  });

  return fetchJson(
    `https://weather.googleapis.com/v1/forecast/hours:lookup?${query.toString()}`,
    { method: "GET" },
    fetchImpl,
  );
}

async function lookupWeatherHistoryHours(location, { apiKey, languageCode, fetchImpl }) {
  const query = new URLSearchParams({
    key: apiKey,
    "location.latitude": String(location.latitude),
    "location.longitude": String(location.longitude),
    languageCode,
    hours: "24",
    pageSize: "24",
  });

  return fetchJson(
    `https://weather.googleapis.com/v1/history/hours:lookup?${query.toString()}`,
    { method: "GET" },
    fetchImpl,
  );
}

async function lookupAirQualityForecastHours(
  location,
  { apiKey, languageCode, fetchImpl, startTime, endTime },
) {
  const query = new URLSearchParams({ key: apiKey });

  return fetchJson(
    `https://airquality.googleapis.com/v1/forecast:lookup?${query.toString()}`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
        period: {
          startTime,
          endTime,
        },
        pageSize: 24,
        universalAqi: true,
        languageCode,
      }),
    },
    fetchImpl,
  );
}

async function lookupAirQualityHistoryHours(
  location,
  { apiKey, languageCode, fetchImpl, hours = 24 },
) {
  const query = new URLSearchParams({ key: apiKey });
  const requestedHours =
    Number.isFinite(hours) && hours > 0 ? Math.min(Math.floor(hours), 168) : 24;

  return fetchJson(
    `https://airquality.googleapis.com/v1/history:lookup?${query.toString()}`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
        hours: requestedHours,
        pageSize: requestedHours,
        universalAqi: true,
        languageCode,
      }),
    },
    fetchImpl,
  );
}

function findAddressComponent(results, acceptedTypes) {
  const requestedTypes = Array.isArray(acceptedTypes)
    ? acceptedTypes
    : [acceptedTypes];

  for (const result of Array.isArray(results) ? results : []) {
    for (const component of Array.isArray(result?.addressComponents)
      ? result.addressComponents
      : []) {
      const componentTypes = Array.isArray(component?.types)
        ? component.types
        : [];

      if (requestedTypes.some((type) => componentTypes.includes(type))) {
        return component;
      }
    }
  }

  return null;
}

function findGeocodeResult(results, acceptedTypes) {
  const requestedTypes = Array.isArray(acceptedTypes)
    ? acceptedTypes
    : [acceptedTypes];

  return (
    (Array.isArray(results) ? results : []).find((result) => {
      const resultTypes = Array.isArray(result?.types) ? result.types : [];
      return requestedTypes.some((type) => resultTypes.includes(type));
    }) || null
  );
}

function resolveCountryDisplayName(regionCode, languageCode) {
  const normalizedRegionCode = normalizeUppercaseString(regionCode);
  if (!normalizedRegionCode) {
    return null;
  }

  try {
    return (
      new Intl.DisplayNames([languageCode, "en"], { type: "region" }).of(
        normalizedRegionCode,
      ) || normalizedRegionCode
    );
  } catch (_error) {
    return normalizedRegionCode;
  }
}

function formatCountryLabel({ countryCode, countryName, languageCode }) {
  const normalizedCountryCode = normalizeUppercaseString(countryCode);
  if (normalizedCountryCode === "GB") {
    return "UK";
  }

  return (
    normalizeOptionalString(countryName) ||
    resolveCountryDisplayName(normalizedCountryCode, languageCode)
  );
}

function extractLocationDetails(geocodeResponse = {}) {
  const results = Array.isArray(geocodeResponse?.results)
    ? geocodeResponse.results
    : [];
  const localityResult = findGeocodeResult(results, [
    "locality",
    "postal_town",
    "administrative_area_level_3",
    "administrative_area_level_2",
    "administrative_area_level_1",
  ]);
  const localityComponent = findAddressComponent(results, [
    "locality",
    "postal_town",
    "administrative_area_level_3",
    "administrative_area_level_2",
    "administrative_area_level_1",
  ]);
  const countryComponent = findAddressComponent(results, "country");
  const formattedLocality = normalizeOptionalString(localityResult?.formattedAddress);
  const postalLocality =
    normalizeOptionalString(localityResult?.postalAddress?.locality) ||
    normalizeOptionalString(localityResult?.displayName?.text) ||
    (formattedLocality
      ? formattedLocality
          .split(",")
          .map((part) => normalizeOptionalString(part))
          .filter(Boolean)[0] || null
      : null) ||
    null;

  return {
    locality:
      normalizeOptionalString(localityComponent?.longText) ||
      normalizeOptionalString(localityComponent?.shortText) ||
      postalLocality,
    countryCode: normalizeUppercaseString(countryComponent?.shortText),
    countryName: normalizeOptionalString(countryComponent?.longText),
  };
}

function buildLocationDisplayName({
  locality,
  countryCode,
  countryName,
  regionCode,
  languageCode,
} = {}) {
  const effectiveCountryCode =
    normalizeUppercaseString(countryCode) || normalizeUppercaseString(regionCode);
  const effectiveCountryName =
    normalizeOptionalString(countryName) ||
    resolveCountryDisplayName(effectiveCountryCode, languageCode);
  const localityLabel = normalizeOptionalString(locality);
  const countryLabel = formatCountryLabel({
    countryCode: effectiveCountryCode,
    countryName: effectiveCountryName,
    languageCode,
  });

  if (localityLabel && countryLabel) {
    return `${localityLabel}, ${countryLabel}`;
  }

  return localityLabel || countryLabel || null;
}

function normalizeMeasurementValue(reading, defaultUnit = null) {
  if (!reading || typeof reading !== "object") {
    return { value: null, unit: defaultUnit };
  }

  const directValue = normalizeFiniteNumber(reading.value);
  if (directValue !== null) {
    return {
      value: directValue,
      unit:
        normalizeOptionalString(reading.units) ||
        normalizeOptionalString(reading.unit) ||
        defaultUnit,
    };
  }

  const degrees = normalizeFiniteNumber(reading.degrees);
  if (degrees !== null) {
    return {
      value: degrees,
      unit: normalizeOptionalString(reading.unit) || defaultUnit,
    };
  }

  const quantity = normalizeFiniteNumber(reading.quantity);
  if (quantity !== null) {
    return {
      value: quantity,
      unit:
        normalizeOptionalString(reading.units) ||
        normalizeOptionalString(reading.unit) ||
        defaultUnit,
    };
  }

  const meanSeaLevelMillibars = normalizeFiniteNumber(
    reading.meanSeaLevelMillibars,
  );
  if (meanSeaLevelMillibars !== null) {
    return {
      value: meanSeaLevelMillibars,
      unit: "MILLIBARS",
    };
  }

  return {
    value: null,
    unit:
      normalizeOptionalString(reading.units) ||
      normalizeOptionalString(reading.unit) ||
      defaultUnit,
  };
}

function buildWeatherObservations(weather, location, retrievedAt) {
  const timezone = weather?.timeZone?.id || null;
  const observationTimestamp = weather?.currentTime || null;
  const locationGranularity = location?.granularity || null;
  const observations = [];

  const temperature = normalizeMeasurementValue(weather?.temperature);
  if (temperature.value !== null) {
    observations.push(
      buildObservation({
        variable: "temperature",
        value: temperature.value,
        unit: temperature.unit,
        observationTimestamp,
        retrievalTimestamp: retrievedAt,
        timezone,
        observationType: "observation",
        source: "weather.current_conditions",
        aggregationPeriod: "instantaneous",
        datasetVersion: "v1",
        locationGranularity,
      }),
    );
  }

  const humidity = normalizeFiniteNumber(weather?.relativeHumidity);
  if (humidity !== null) {
    observations.push(
      buildObservation({
        variable: "relative_humidity",
        value: humidity,
        unit: "PERCENT",
        observationTimestamp,
        retrievalTimestamp: retrievedAt,
        timezone,
        observationType: "observation",
        source: "weather.current_conditions",
        aggregationPeriod: "instantaneous",
        datasetVersion: "v1",
        locationGranularity,
      }),
    );
  }

  const pressure = normalizeMeasurementValue(weather?.airPressure || weather?.seaLevelPressure);
  if (pressure.value !== null) {
    observations.push(
      buildObservation({
        variable: "atmospheric_pressure",
        value: pressure.value,
        unit: pressure.unit,
        observationTimestamp,
        retrievalTimestamp: retrievedAt,
        timezone,
        observationType: "observation",
        source: "weather.current_conditions",
        aggregationPeriod: "instantaneous",
        datasetVersion: "v1",
        locationGranularity,
      }),
    );
  }

  return observations;
}

function buildAirQualityObservations(airQuality, location, retrievedAt) {
  const observationTimestamp = airQuality?.dateTime || null;
  const timezone = null;
  const locationGranularity = location?.granularity || null;
  const observations = [];
  const universalIndex = Array.isArray(airQuality?.indexes)
    ? airQuality.indexes.find((entry) =>
        String(entry?.code || "").toUpperCase().includes("UAQI"),
      ) || airQuality.indexes[0]
    : null;

  if (universalIndex && normalizeFiniteNumber(universalIndex.aqi) !== null) {
    observations.push(
      buildObservation({
        variable: "aqi",
        value: universalIndex.aqi,
        unit: "INDEX",
        observationTimestamp,
        retrievalTimestamp: retrievedAt,
        timezone,
        observationType: "observation",
        source: "air_quality.current_conditions",
        aqiStandard:
          normalizeOptionalString(universalIndex.displayName) ||
          normalizeOptionalString(universalIndex.code) ||
          "UAQI",
        aggregationPeriod: "hourly",
        qualityStatus: normalizeOptionalString(universalIndex.category) || "available",
        datasetVersion: "v1",
        locationGranularity,
      }),
    );
  }

  (Array.isArray(airQuality?.pollutants) ? airQuality.pollutants : []).forEach(
    (pollutant) => {
      const concentration = normalizeMeasurementValue(pollutant?.concentration);
      const code = normalizeOptionalString(pollutant?.code);
      if (!code || concentration.value === null) {
        return;
      }

      observations.push(
        buildObservation({
          variable: code.toLowerCase(),
          value: concentration.value,
          unit: concentration.unit,
          observationTimestamp,
          retrievalTimestamp: retrievedAt,
          timezone,
          observationType: "observation",
          source: "air_quality.current_conditions",
          aggregationPeriod: "hourly",
          datasetVersion: "v1",
          locationGranularity,
        }),
      );
    },
  );

  return observations;
}

function buildPollenObservations(pollen, location, retrievedAt) {
  const locationGranularity = location?.granularity || null;
  const dailyInfo = Array.isArray(pollen?.dailyInfo) ? pollen.dailyInfo[0] : null;
  if (!dailyInfo) {
    return [];
  }

  const observationTimestamp =
    dailyInfo?.date?.year && dailyInfo?.date?.month && dailyInfo?.date?.day
      ? new Date(
          Date.UTC(
            dailyInfo.date.year,
            dailyInfo.date.month - 1,
            dailyInfo.date.day,
          ),
        ).toISOString()
      : null;

  return (Array.isArray(dailyInfo?.pollenTypeInfo) ? dailyInfo.pollenTypeInfo : [])
    .map((entry) => {
      const code = normalizeOptionalString(entry?.code);
      const indexValue = normalizeFiniteNumber(entry?.indexInfo?.value);
      if (!code || indexValue === null) {
        return null;
      }

      return buildObservation({
        variable: `${code.toLowerCase()}_pollen_index`,
        value: indexValue,
        unit: "INDEX",
        observationTimestamp,
        retrievalTimestamp: retrievedAt,
        timezone: null,
        observationType: "forecast",
        source: "pollen.forecast",
        aggregationPeriod: "daily",
        qualityStatus: "forecast",
        datasetVersion: "v1",
        locationGranularity,
        provisional: true,
      });
    })
    .filter(Boolean);
}

function findPreferredAirQualityIndex(indexes) {
  const list = Array.isArray(indexes) ? indexes : [];

  return (
    list.find((entry) =>
      String(entry?.code || "").toUpperCase().includes("UAQI"),
    ) ||
    list[0] ||
    null
  );
}

function extractWeatherTimelineSamples(entries, metricKey, source, observationType) {
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      const timestamp = normalizeOptionalString(entry?.interval?.startTime);
      if (!timestamp) {
        return null;
      }

      if (metricKey === "temperature") {
        const reading = normalizeMeasurementValue(entry?.temperature);
        if (reading.value === null) {
          return null;
        }

        return {
          timestamp,
          value: reading.value,
          unit: reading.unit,
          source,
          observationType,
        };
      }

      if (metricKey === "humidity") {
        const value = normalizeFiniteNumber(entry?.relativeHumidity);
        if (value === null) {
          return null;
        }

        return {
          timestamp,
          value,
          unit: "PERCENT",
          source,
          observationType,
        };
      }

      return null;
    })
    .filter(Boolean);
}

function extractAirQualityTimelineSamples(entries, source, observationType) {
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      const timestamp = normalizeOptionalString(entry?.dateTime);
      const index = findPreferredAirQualityIndex(entry?.indexes);
      const value = normalizeFiniteNumber(index?.aqi);

      if (!timestamp || value === null) {
        return null;
      }

      return {
        timestamp,
        value,
        unit: "INDEX",
        source,
        observationType,
        qualityStatus: normalizeOptionalString(index?.category),
      };
    })
    .filter(Boolean);
}

function findPreferredPollenTimelineEntry(pollen) {
  const dailyInfo = Array.isArray(pollen?.dailyInfo) ? pollen.dailyInfo[0] : null;
  const pollenTypeInfo = Array.isArray(dailyInfo?.pollenTypeInfo)
    ? dailyInfo.pollenTypeInfo
    : [];
  const preferredCodeOrder = ["TREE", "GRASS", "WEED"];

  for (const code of preferredCodeOrder) {
    const match = pollenTypeInfo.find(
      (entry) => normalizeUppercaseString(entry?.code) === code,
    );
    const value = normalizeFiniteNumber(match?.indexInfo?.value);
    if (match && value !== null) {
      return {
        timestamp:
          dailyInfo?.date?.year && dailyInfo?.date?.month && dailyInfo?.date?.day
            ? new Date(
                Date.UTC(
                  dailyInfo.date.year,
                  dailyInfo.date.month - 1,
                  dailyInfo.date.day,
                ),
              ).toISOString()
            : null,
        value,
        unit: "INDEX",
        source: "pollen.forecast",
        observationType: "forecast",
        qualityStatus:
          normalizeOptionalString(match?.indexInfo?.category) || "forecast",
      };
    }
  }

  return null;
}

function buildMetricTimeline(metricKey, samples, { timeZone, referenceTimestamp, resolution }) {
  const currentDayKey = buildLocalDayKey(referenceTimestamp, timeZone);
  if (!currentDayKey) {
    return null;
  }

  const slots = buildEmptyTimelineSlots();
  let hasValue = false;

  (Array.isArray(samples) ? samples : []).forEach((sample) => {
    const timestamp = normalizeOptionalString(sample?.timestamp);
    const value = normalizeFiniteNumber(sample?.value);

    if (!timestamp || value === null) {
      return;
    }

    if (buildLocalDayKey(timestamp, timeZone) !== currentDayKey) {
      return;
    }

    const parts = readZonedDateParts(timestamp, timeZone);
    if (!parts || !Number.isInteger(parts.hour) || parts.hour < 0 || parts.hour > 23) {
      return;
    }

    slots[parts.hour] = {
      hour: parts.hour,
      value,
      unit: normalizeOptionalString(sample?.unit),
      timestamp,
      source: normalizeOptionalString(sample?.source),
      observationType: normalizeOptionalString(sample?.observationType),
      qualityStatus: normalizeOptionalString(sample?.qualityStatus),
    };
    hasValue = true;
  });

  if (!hasValue) {
    return null;
  }

  return {
    metricKey,
    resolution: normalizeOptionalString(resolution) || "hourly",
    slots,
  };
}

function buildFlatMetricTimeline(metricKey, sample, { resolution = "daily" } = {}) {
  const value = normalizeFiniteNumber(sample?.value);
  if (value === null) {
    return null;
  }

  return {
    metricKey,
    resolution,
    slots: buildEmptyTimelineSlots().map((slot) => ({
      ...slot,
      value,
      unit: normalizeOptionalString(sample?.unit),
      timestamp: normalizeOptionalString(sample?.timestamp),
      source: normalizeOptionalString(sample?.source),
      observationType: normalizeOptionalString(sample?.observationType),
      qualityStatus: normalizeOptionalString(sample?.qualityStatus),
    })),
  };
}

function buildHomeTimelines({
  referenceTimestamp,
  timeZone,
  pollen = null,
  weatherHistory = null,
  weatherForecast = null,
  airQualityHistory = null,
  airQualityForecast = null,
} = {}) {
  const resolvedTimeZone = normalizeOptionalString(timeZone) || readBrowserTimeZone() || "UTC";
  const resolvedReferenceTimestamp =
    normalizeOptionalString(referenceTimestamp) || new Date().toISOString();
  const metrics = {};

  const temperatureTimeline = buildMetricTimeline(
    "temperature",
    [
      ...extractWeatherTimelineSamples(
        weatherHistory?.historyHours,
        "temperature",
        "weather.history_hours",
        "history",
      ),
      ...extractWeatherTimelineSamples(
        weatherForecast?.forecastHours,
        "temperature",
        "weather.forecast_hours",
        "forecast",
      ),
    ],
    {
      timeZone: resolvedTimeZone,
      referenceTimestamp: resolvedReferenceTimestamp,
      resolution: "hourly",
    },
  );
  if (temperatureTimeline) {
    metrics.temperature = temperatureTimeline;
  }

  const humidityTimeline = buildMetricTimeline(
    "humidity",
    [
      ...extractWeatherTimelineSamples(
        weatherHistory?.historyHours,
        "humidity",
        "weather.history_hours",
        "history",
      ),
      ...extractWeatherTimelineSamples(
        weatherForecast?.forecastHours,
        "humidity",
        "weather.forecast_hours",
        "forecast",
      ),
    ],
    {
      timeZone: resolvedTimeZone,
      referenceTimestamp: resolvedReferenceTimestamp,
      resolution: "hourly",
    },
  );
  if (humidityTimeline) {
    metrics.humidity = humidityTimeline;
  }

  const airQualityTimeline = buildMetricTimeline(
    "aqi",
    [
      ...extractAirQualityTimelineSamples(
        airQualityHistory?.hoursInfo,
        "air_quality.history",
        "history",
      ),
      ...extractAirQualityTimelineSamples(
        airQualityForecast?.hourlyForecasts,
        "air_quality.forecast",
        "forecast",
      ),
    ],
    {
      timeZone: resolvedTimeZone,
      referenceTimestamp: resolvedReferenceTimestamp,
      resolution: "hourly",
    },
  );
  if (airQualityTimeline) {
    metrics.aqi = airQualityTimeline;
  }

  const pollenTimeline = buildFlatMetricTimeline(
    "pollen",
    findPreferredPollenTimelineEntry(pollen),
    { resolution: "daily" },
  );
  if (pollenTimeline) {
    metrics.pollen = pollenTimeline;
  }

  if (!Object.keys(metrics).length) {
    return null;
  }

  return {
    currentDay: {
      date: buildLocalDayKey(resolvedReferenceTimestamp, resolvedTimeZone),
      timeZone: resolvedTimeZone,
      currentHourOffset: buildCurrentHourOffset(
        resolvedReferenceTimestamp,
        resolvedTimeZone,
      ),
      metrics,
    },
  };
}

export async function captureCurrentEnvironmentalContext({
  purpose = "home",
  env = process.env,
  fetchImpl = fetch,
  geolocation,
  includeDailyTimelines = false,
} = {}) {
  if (!isEnvironmentalContextEnabled(env)) {
    return null;
  }

  const apiKey = getGoogleMapsApiKey(env);
  const languageCode = readLanguageCode();
  const retrievedAt = new Date().toISOString();

  let location;
  try {
    location = await getCurrentCoarseLocation({ geolocation, env });
  } catch (error) {
    return buildUnavailableContext({
      purpose,
      source: "browser_geolocation",
      code: normalizeOptionalString(error?.message) || "unavailable",
      message: "Environmental context could not access browser geolocation.",
    });
  }

  const weatherPromise = lookupWeather(location, {
    apiKey,
    languageCode,
    fetchImpl,
  });
  const airQualityPromise = lookupAirQuality(location, {
    apiKey,
    languageCode,
    fetchImpl,
  });
  const pollenPromise = lookupPollen(location, {
    apiKey,
    languageCode,
    fetchImpl,
  });
  const locationDetailsPromise = lookupLocationDetails(location, {
    apiKey,
    languageCode,
    fetchImpl,
  });

  const [weatherResult, airQualityResult, pollenResult, locationDetailsResult] =
    await Promise.allSettled([
      weatherPromise,
      airQualityPromise,
      pollenPromise,
      locationDetailsPromise,
    ]);

  const observations = [];
  const limitations = [];
  const errors = [];

  if (weatherResult.status === "fulfilled") {
    observations.push(
      ...buildWeatherObservations(weatherResult.value, location, retrievedAt),
    );
  } else {
    errors.push({
      source: "weather.current_conditions",
      code: weatherResult.reason?.message || "lookup_failed",
      message: "Weather conditions were unavailable.",
    });
  }

  if (airQualityResult.status === "fulfilled") {
    observations.push(
      ...buildAirQualityObservations(
        airQualityResult.value,
        location,
        retrievedAt,
      ),
    );
  } else {
    errors.push({
      source: "air_quality.current_conditions",
      code: airQualityResult.reason?.message || "lookup_failed",
      message: "Air quality conditions were unavailable.",
    });
  }

  if (pollenResult.status === "fulfilled") {
    observations.push(
      ...buildPollenObservations(pollenResult.value, location, retrievedAt),
    );
    if (Array.isArray(pollenResult.value?.dailyInfo) && pollenResult.value.dailyInfo.length) {
      limitations.push("forecast");
    }
  } else {
    errors.push({
      source: "pollen.forecast",
      code: pollenResult.reason?.message || "lookup_failed",
      message: "Pollen forecast was unavailable.",
    });
  }

  const locationContext = {
    latitude: location.latitude,
    longitude: location.longitude,
    source: location.source,
    accuracyMeters: location.accuracyMeters,
    accuracyBucket: location.accuracyBucket,
    granularity: location.granularity,
    locationObtainedAt: location.locationObtainedAt,
    timezone:
      weatherResult.status === "fulfilled"
        ? weatherResult.value?.timeZone?.id || null
        : null,
    regionCode:
      airQualityResult.status === "fulfilled"
        ? airQualityResult.value?.regionCode || null
        : pollenResult.status === "fulfilled"
          ? pollenResult.value?.regionCode || null
          : null,
  };
  const geocodeLocationDetails =
    locationDetailsResult.status === "fulfilled"
      ? extractLocationDetails(locationDetailsResult.value)
      : null;
  const resolvedCountryCode =
    normalizeUppercaseString(geocodeLocationDetails?.countryCode) ||
    normalizeUppercaseString(locationContext.regionCode);
  const resolvedCountryName =
    normalizeOptionalString(geocodeLocationDetails?.countryName) ||
    resolveCountryDisplayName(resolvedCountryCode, languageCode);

  locationContext.locality = geocodeLocationDetails?.locality || null;
  locationContext.countryCode = resolvedCountryCode;
  locationContext.countryName = resolvedCountryName;
  locationContext.displayName = buildLocationDisplayName({
    locality: geocodeLocationDetails?.locality,
    countryCode: resolvedCountryCode,
    countryName: resolvedCountryName,
    regionCode: locationContext.regionCode,
    languageCode,
  });
  let timelines = null;

  if (includeDailyTimelines) {
    const referenceTimestamp =
      normalizeOptionalString(weatherResult.value?.currentTime) || retrievedAt;
    const roundedReferenceHour = floorToUtcHourIso(referenceTimestamp);
    const nextReferenceHour = ceilToUtcHourIso(referenceTimestamp);
    const forecastEndTime = shiftIsoHours(nextReferenceHour, 23);

    const [
      weatherHistoryResult,
      weatherForecastResult,
      airQualityHistoryResult,
      airQualityForecastResult,
    ] = await Promise.allSettled([
      lookupWeatherHistoryHours(location, {
        apiKey,
        languageCode,
        fetchImpl,
      }),
      lookupWeatherForecastHours(location, {
        apiKey,
        languageCode,
        fetchImpl,
      }),
      lookupAirQualityHistoryHours(location, {
        apiKey,
        languageCode,
        fetchImpl,
        hours: 24,
      }),
      lookupAirQualityForecastHours(location, {
        apiKey,
        languageCode,
        fetchImpl,
        startTime: nextReferenceHour,
        endTime: forecastEndTime,
      }),
    ]);

    if (weatherHistoryResult.status !== "fulfilled") {
      errors.push({
        source: "weather.history_hours",
        code: weatherHistoryResult.reason?.message || "lookup_failed",
        message: "Hourly weather history was unavailable.",
      });
    }

    if (weatherForecastResult.status !== "fulfilled") {
      errors.push({
        source: "weather.forecast_hours",
        code: weatherForecastResult.reason?.message || "lookup_failed",
        message: "Hourly weather forecast was unavailable.",
      });
    }

    if (airQualityHistoryResult.status !== "fulfilled") {
      errors.push({
        source: "air_quality.history",
        code: airQualityHistoryResult.reason?.message || "lookup_failed",
        message: "Hourly air quality history was unavailable.",
      });
    }

    if (airQualityForecastResult.status !== "fulfilled") {
      errors.push({
        source: "air_quality.forecast",
        code: airQualityForecastResult.reason?.message || "lookup_failed",
        message: "Hourly air quality forecast was unavailable.",
      });
    }

    timelines = buildHomeTimelines({
      referenceTimestamp,
      timeZone: locationContext.timezone,
      pollen: pollenResult.status === "fulfilled" ? pollenResult.value : null,
      weatherHistory:
        weatherHistoryResult.status === "fulfilled"
          ? weatherHistoryResult.value
          : null,
      weatherForecast:
        weatherForecastResult.status === "fulfilled"
          ? weatherForecastResult.value
          : null,
      airQualityHistory:
        airQualityHistoryResult.status === "fulfilled"
          ? airQualityHistoryResult.value
          : null,
      airQualityForecast:
        airQualityForecastResult.status === "fulfilled"
          ? airQualityForecastResult.value
          : null,
    });

    if (timelines?.currentDay?.metrics?.pollen?.resolution === "daily") {
      limitations.push("pollen_daily_timeline");
    }
  }

  return buildBaseContext({
    purpose,
    status:
      observations.length > 0 && errors.length === 0
        ? "available"
        : observations.length > 0
          ? "partial"
          : "unavailable",
    location: locationContext,
    retrievedAt,
    observations,
    timelines,
    limitations,
    errors,
  });
}

export function summarizeEnvironmentalContext(context) {
  const observations = Array.isArray(context?.observations)
    ? context.observations
    : [];
  const findObservation = (variable) =>
    observations.find((entry) => entry?.variable === variable) || null;

  return {
    temperature: findObservation("temperature"),
    humidity: findObservation("relative_humidity"),
    pressure: findObservation("atmospheric_pressure"),
    aqi: findObservation("aqi"),
    pollen:
      findObservation("tree_pollen_index") ||
      findObservation("grass_pollen_index") ||
      findObservation("weed_pollen_index"),
  };
}
